import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { ProductCard } from '@/components/ProductCard';
import { Seo } from '@/components/Seo';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { SlidersHorizontal, X } from 'lucide-react';

const MULTI = ['category', 'subcategory', 'type', 'region', 'estate', 'format', 'rarity', 'availability', 'vintage'];

function FilterGroup({ title, field, options, selected, onToggle }) {
  if (!options || options.length === 0) return null;
  return (
    <AccordionItem value={field} className="border-nw-gold/15">
      <AccordionTrigger className="text-[11px] tracking-luxe uppercase text-nw-ivory hover:text-nw-gold hover:no-underline py-4">{title}</AccordionTrigger>
      <AccordionContent>
        <div className="space-y-2.5 pb-2 max-h-56 overflow-y-auto pr-2">
          {options.map((o) => (
            <label key={o} data-testid={`filter-${field}-${o}`} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox checked={selected.includes(String(o))} onCheckedChange={() => onToggle(field, String(o))}
                className="border-nw-gold/40 data-[state=checked]:bg-nw-gold data-[state=checked]:border-nw-gold data-[state=checked]:text-nw-black" />
              <span className="text-sm text-nw-warmgrey group-hover:text-nw-ivory transition-colors capitalize">{o}</span>
            </label>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

export default function Collections() {
  const { t, lang } = useStore();
  const [params, setParams] = useSearchParams();
  const [facets, setFacets] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [price, setPrice] = useState([0, 100000]);
  const [mobileOpen, setMobileOpen] = useState(false);

  const selected = useMemo(() => {
    const s = {};
    MULTI.forEach((f) => { const v = params.get(f); s[f] = v ? v.split(',') : []; });
    return s;
  }, [params]);
  const search = params.get('search') || '';
  const sort = params.get('sort') || 'featured';

  // Search-engine metadata follows the active filter so every catalogue view has a unique title
  const seo = useMemo(() => {
    const fr = lang === 'fr';
    const cat = params.get('category'); const rar = params.get('rarity'); const typ = params.get('type'); const sub = params.get('subcategory');
    let title = fr ? 'Collections — Grands vins & spiritueux rares' : 'Collections — Fine wines & rare spirits';
    let desc = fr ? 'Explorez la sélection NICEWINE : grands crus de Bordeaux et Bourgogne, champagnes de prestige, cognacs, whiskies et rhums de collection. Livraison assurée dans le monde entier.'
      : 'Explore the NICEWINE selection: Bordeaux and Burgundy grands crus, prestige champagnes, collectible cognacs, whiskies and rums. Insured worldwide delivery.';
    if (search) { title = fr ? `Recherche « ${search} »` : `Search "${search}"`; }
    else if (cat === 'wine') { title = fr ? 'Vins — Grands crus & millésimes rares' : 'Wines — Grands crus & rare vintages'; desc = fr ? 'Bordeaux, Bourgogne, Champagne, Rhône et Loire : les plus grands vins, sélectionnés et conservés en cave climatisée par NICEWINE.' : 'Bordeaux, Burgundy, Champagne, Rhône and Loire: the greatest wines, selected and cellared by NICEWINE.'; }
    else if (cat === 'spirit') { title = fr ? 'Spiritueux — Cognacs, whiskies & rhums d’exception' : 'Spirits — Exceptional cognacs, whiskies & rums'; desc = fr ? 'Louis XIII, Hennessy Paradis, Macallan, Yamazaki, Laphroaig, armagnacs millésimés et rhums de collection, authentifiés par NICEWINE.' : 'Louis XIII, Hennessy Paradis, Macallan, Yamazaki, Laphroaig, vintage armagnacs and collectible rums, authenticated by NICEWINE.'; }
    else if (rar === 'collector') { title = fr ? 'Collections rares — Bouteilles de légende' : 'Rare collections — Legendary bottles'; desc = fr ? 'Pétrus 1947, Romanée-Conti 1945, Château Margaux 1989 Impériale, Macallan Fine & Rare : les trésors de cave NICEWINE, avec dossier de provenance.' : 'Pétrus 1947, Romanée-Conti 1945, Château Margaux 1989 Impériale, Macallan Fine & Rare: NICEWINE cellar treasures with provenance dossier.'; }
    else if (typ === 'red') { title = fr ? 'Vins rouges — Grands crus classés' : 'Red wines — Classified growths'; }
    else if (typ === 'white') { title = fr ? 'Vins blancs — Bourgognes & grands blancs' : 'White wines — Burgundies & fine whites'; }
    else if (sub === 'Champagne') { title = fr ? 'Champagnes de prestige' : 'Prestige champagnes'; }
    const qs = params.toString();
    return { title, desc, path: `/collections${qs ? `?${qs}` : ''}` };
  }, [params, lang, search]);
  const page = Number(params.get('page') || 1);

  useEffect(() => { api.facets().then((f) => { setFacets(f); setPrice([f.price_min, f.price_max]); }); }, []);

  useEffect(() => {
    setLoading(true);
    const query = {};
    MULTI.forEach((f) => { if (selected[f].length) query[f] = selected[f].join(','); });
    if (search) query.search = search;
    if (params.get('min_price')) query.min_price = params.get('min_price');
    if (params.get('max_price')) query.max_price = params.get('max_price');
    query.sort = sort; query.page = page; query.limit = 12;
    api.products(query).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [params]);

  const update = (mut) => {
    const next = new URLSearchParams(params);
    mut(next);
    next.set('page', '1');
    setParams(next);
  };
  const toggle = (field, val) => update((next) => {
    const cur = (next.get(field) || '').split(',').filter(Boolean);
    const nv = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    if (nv.length) next.set(field, nv.join(',')); else next.delete(field);
  });
  const applyPrice = () => update((next) => {
    next.set('min_price', String(Math.round(price[0])));
    next.set('max_price', String(Math.round(price[1])));
  });
  const clearAll = () => setParams(new URLSearchParams());
  const activeCount = MULTI.reduce((n, f) => n + selected[f].length, 0) + (params.get('min_price') ? 1 : 0) + (search ? 1 : 0);

  const FiltersInner = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="nw-eyebrow">{t.catalog.filters}</span>
        {activeCount > 0 && <button data-testid="catalog-clear-filters" onClick={clearAll} className="text-[11px] text-nw-gold hover:underline flex items-center gap-1"><X size={12} /> {t.catalog.clear}</button>}
      </div>
      <Accordion type="multiple" defaultValue={['category', 'type', 'region']} className="w-full">
        <AccordionItem value="price" className="border-nw-gold/15">
          <AccordionTrigger className="text-[11px] tracking-luxe uppercase text-nw-ivory hover:text-nw-gold hover:no-underline py-4">{t.catalog.price}</AccordionTrigger>
          <AccordionContent>
            <div className="px-1 pt-2 pb-4">
              <Slider data-testid="catalog-filter-price-slider" min={facets?.price_min || 0} max={facets?.price_max || 100000} step={50}
                value={price} onValueChange={setPrice} className="my-4" />
              <div className="flex items-center justify-between text-xs text-nw-warmgrey">
                <span>{Math.round(price[0]).toLocaleString()} €</span>
                <span>{Math.round(price[1]).toLocaleString()} €</span>
              </div>
              <button onClick={applyPrice} className="nw-btn-gold w-full mt-3 h-9">{t.catalog.apply}</button>
            </div>
          </AccordionContent>
        </AccordionItem>
        <FilterGroup title={t.catalog.category} field="category" options={facets?.categories} selected={selected.category} onToggle={toggle} />
        <FilterGroup title={t.catalog.type} field="type" options={facets?.types} selected={selected.type} onToggle={toggle} />
        <FilterGroup title="Appellation" field="subcategory" options={facets?.subcategories} selected={selected.subcategory} onToggle={toggle} />
        <FilterGroup title={t.catalog.region} field="region" options={facets?.regions} selected={selected.region} onToggle={toggle} />
        <FilterGroup title={t.catalog.estate} field="estate" options={facets?.estates} selected={selected.estate} onToggle={toggle} />
        <FilterGroup title={t.catalog.vintage} field="vintage" options={facets?.vintages} selected={selected.vintage} onToggle={toggle} />
        <FilterGroup title={t.catalog.format} field="format" options={facets?.formats} selected={selected.format} onToggle={toggle} />
        <FilterGroup title={t.catalog.rarity} field="rarity" options={facets?.rarities} selected={selected.rarity} onToggle={toggle} />
        <FilterGroup title={t.catalog.availability} field="availability" options={facets?.availabilities} selected={selected.availability} onToggle={toggle} />
      </Accordion>
    </div>
  );

  return (
    <div className="bg-nw-black">
      <Seo title={seo.title} description={seo.desc} path={seo.path} lang={lang} noindex={!!search} />
      <div className="relative py-16 border-b border-nw-gold/12">
        <div className="nw-container text-center">
          <div className="nw-eyebrow mb-3">NICEWINE</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-nw-ivory">{search ? `\u00ab ${search} \u00bb` : t.catalog.title}</h1>
        </div>
      </div>
      <div className="nw-container py-10">
        <div className="lg:grid lg:grid-cols-[260px_1fr] lg:gap-10">
          <aside className="hidden lg:block">
            <div className="sticky top-[92px]"><FiltersInner /></div>
          </aside>
          <div>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
              <div className="text-sm text-nw-warmgrey whitespace-nowrap"><span className="text-nw-ivory">{data.total}</span> {t.catalog.results}</div>
              <div className="flex items-center gap-2 sm:gap-3 ml-auto">
                <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                  <SheetTrigger asChild>
                    <button data-testid="catalog-filters-open-button" className="lg:hidden nw-btn-gold h-10 px-4 relative">
                      <SlidersHorizontal size={15} /> {t.catalog.filters}
                      {activeCount > 0 && <span className="ml-1 text-nw-gold">({activeCount})</span>}
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="bg-nw-black border-nw-gold/20 text-nw-ivory w-[88vw] max-w-sm overflow-y-auto">
                    <SheetHeader><SheetTitle className="text-nw-gold font-serif text-xl text-left">{t.catalog.filters}</SheetTitle></SheetHeader>
                    <div className="mt-4"><FiltersInner /></div>
                    <button onClick={() => setMobileOpen(false)} className="nw-btn-ivory w-full mt-6">{t.catalog.apply}</button>
                  </SheetContent>
                </Sheet>
                <Select value={sort} onValueChange={(v) => update((n) => n.set('sort', v))}>
                  <SelectTrigger data-testid="catalog-sort-select" className="w-[150px] sm:w-[190px] h-10 bg-transparent border-nw-gold/25 text-nw-ivory text-xs tracking-wide2 uppercase"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory">
                    <SelectItem value="featured">{t.catalog.sortFeatured}</SelectItem>
                    <SelectItem value="price_asc">{t.catalog.sortPriceAsc}</SelectItem>
                    <SelectItem value="price_desc">{t.catalog.sortPriceDesc}</SelectItem>
                    <SelectItem value="name_asc">{t.catalog.sortName}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="bg-nw-espresso animate-pulse" style={{ aspectRatio: '3/4' }} />)}
              </div>
            ) : data.items.length === 0 ? (
              <div className="py-24 text-center text-nw-warmgrey">{t.catalog.none}</div>
            ) : (
              <div data-testid="catalog-product-grid" className="grid grid-cols-2 md:grid-cols-3 gap-5 lg:gap-6">
                {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
            {data.pages > 1 && (
              <div data-testid="catalog-pagination" className="flex items-center justify-center gap-2 mt-12">
                {Array.from({ length: data.pages }).map((_, i) => (
                  <button key={i} data-testid={`page-${i + 1}`} onClick={() => update((n) => n.set('page', String(i + 1)))}
                    className={`w-9 h-9 text-sm border transition-colors ${page === i + 1 ? 'border-nw-gold text-nw-gold' : 'border-nw-gold/20 text-nw-warmgrey hover:text-nw-ivory'}`}>{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
